import DynmBtn from '@wconcept/wconcept-ui-renewal/src/components/molecules/_btn/DynmBtn';
import FilterChipBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/FilterChipBtn';
import {
  IconWOnly02Black,
  IconWOnly02White,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import RenewalLpFilterBar from '@wconcept/wconcept-ui-renewal/components/organisms/_list/LpFilterBar';
import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';
import type { AggsOption } from '@wconcept/wconcept-common/models/display/search';
import CategoryMenuBar from '@wconcept/wconcept-ui-renewal/src/components/organisms/_menuBar/CategoryMenuBar';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/MenuBarItem';
import { useSearchParams } from 'next/navigation';
import { MutableRefObject, useCallback, useMemo } from 'react';
import {
  getLpFilterBarGroups,
  getLpQuickFilterTextColor,
  getLpVisibleSelectedFilters,
} from '@utils/lpFilterBarModel';
import type {
  LpProductCountResult,
  LpProductFilterResult,
  LpProductFilterType,
  LpProductFilters,
  LpProductGroupType,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import useLpFilterModalV2 from './hooks/useLpFilterModalV2';
import {
  getLpProductV2QuickFilterQueryString,
  isLpProductV2FilterBarItemSelected,
} from './filterV2';

interface LpFilterBarV2Props {
  readonly list: FilterBarItem[];
  readonly lastFilterTabRef: MutableRefObject<string | undefined>;
  readonly selectedFilterList?: FilterItem[];
  readonly originalFilters?: LpProductFilters;
  readonly onResetFilter?: () => void;
  readonly onFilterSubmit?: (
    queryParam: string,
    selectedFilterList?: FilterItem[],
    type?: string
  ) => void;
  readonly requestFilter: (
    type: LpProductFilterType,
    params: LpProductRequest
  ) => Promise<LpProductFilterResult | undefined>;
  readonly requestCount: (
    params: LpProductRequest
  ) => Promise<LpProductCountResult | undefined>;
  readonly onClickPromoBtn?: (item: FilterBarItem) => void;
  readonly onFilterClose?: () => void;
  readonly hideFilterOptionCounts?: boolean;
  readonly hiddenSelectedFilterTypes?: readonly string[];
  readonly isCategoryMenu?: boolean;
  readonly dcRateValue?: string;
  readonly dcRateGroupInfo?: AggsOption[];
  readonly onDcRateGroupChange?: (item: AggsOption, index: number) => void;
}

const getFilterText = (filter: FilterItem): string =>
  filter.name ||
  (typeof filter.value === 'string' ? filter.value : undefined) ||
  filter.code ||
  '';

const getQuickFilterQueryKey = (
  item: FilterBarItem
): LpProductGroupType | 'saleTags' => {
  if (
    'queryKey' in item &&
    (item.queryKey === 'productType' || item.queryKey === 'benefit')
  ) {
    return item.queryKey;
  }

  return 'saleTags';
};

function LpFilterBarV2({
  list,
  lastFilterTabRef,
  selectedFilterList = [],
  originalFilters,
  onResetFilter,
  onFilterSubmit,
  requestFilter,
  requestCount,
  onClickPromoBtn,
  onFilterClose,
  hideFilterOptionCounts,
  hiddenSelectedFilterTypes,
  isCategoryMenu,
  dcRateValue,
  dcRateGroupInfo,
  onDcRateGroupChange,
}: LpFilterBarV2Props): React.JSX.Element {
  const searchParams = useSearchParams();
  const { emphasis, normal } = useMemo(
    () => getLpFilterBarGroups(list),
    [list]
  );
  const visibleSelectedFilters = useMemo(
    () =>
      getLpVisibleSelectedFilters(
        selectedFilterList,
        list,
        hiddenSelectedFilterTypes
      ),
    [hiddenSelectedFilterTypes, list, selectedFilterList]
  );

  const { openFilterModal, submit } = useLpFilterModalV2({
    filterBarList: list,
    lastFilterTabRef,
    selectedFilters: selectedFilterList,
    originalFilters,
    onFilterSubmit,
    onFilterClose,
    requestProductAggByType: requestFilter,
    requestProductCount: requestCount,
    hideFilterOptionCounts,
    hiddenSelectedFilterTypes,
  });

  const onQuickFilterClick = useCallback(
    (item: FilterBarItem) => {
      const isSelecting = !item.activeText;
      onFilterSubmit?.(
        getLpProductV2QuickFilterQueryString(
          searchParams,
          isSelecting,
          item.value,
          getQuickFilterQueryKey(item)
        ),
        undefined,
        'quick'
      );
      if (isSelecting) {
        onClickPromoBtn?.(item);
      }
    },
    [onClickPromoBtn, onFilterSubmit, searchParams]
  );

  const filterItems = normal.map(item => (
    <DynmBtn
      key={item.value}
      mode="filter"
      text={item.text}
      type={item.type}
      active={isLpProductV2FilterBarItemSelected(item, selectedFilterList)}
      onClick={() => openFilterModal(item)}
    />
  ));
  const quickItems = emphasis.map(item => {
    const isExclusive = item.value === 'exclusive';
    return (
      <FilterChipBtn
        key={item.value}
        text={item.text}
        textColor={getLpQuickFilterTextColor(item.value)}
        imgSrc={isExclusive ? <IconWOnly02Black /> : undefined}
        activeImgSrc={isExclusive ? <IconWOnly02White /> : undefined}
        active={Boolean(item.activeText)}
        onClick={() => onQuickFilterClick(item)}
      />
    );
  });
  const selectedItems = visibleSelectedFilters.map((filter, index) => (
    <DynmBtn
      key={`${filter.filterType}-${filter.code || index}`}
      mode="filter"
      text={getFilterText(filter)}
      onClose={() =>
        submit(
          selectedFilterList.filter(item => item !== filter),
          'remove'
        )
      }
    />
  ));

  const filterBar = (
    <RenewalLpFilterBar
      quickItems={quickItems}
      filterItems={filterItems}
      selectedItems={selectedItems}
      onReset={onResetFilter}
    />
  );

  if (!isCategoryMenu) return filterBar;

  return (
    <div className="area-menu">
      <div className="inner-area-menu">
        <CategoryMenuBar
          type="type5"
          selectedValue={dcRateValue}
          items={(dcRateGroupInfo ?? []).map((item, index) => (
            <MenuBarItem
              key={item.code}
              value={item.code}
              text={item.name}
              onClick={() => onDcRateGroupChange?.(item, index)}
            />
          ))}
        />
        {filterBar}
      </div>
    </div>
  );
}

export default LpFilterBarV2;
